import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Phone, ArrowRight, Play, Pause } from 'lucide-react';
import WhatsAppIcon from '@/components/WhatsAppIcon';
import HeroExamSearch from '@/components/HeroExamSearch';
import { listRows } from '@/api/supabaseClient';
import { usePublicRoutes } from '@/lib/publicRoutes';
import { useCdiAge } from '@/hooks/useCdiAge';
import {
  HERO_FALLBACK_BADGE,
  canAutoplayHeroVideo,
  heroSlideDurationMs,
  isHeroVideoSlide,
  resolveHeroSlide,
  resolveHeroSwipe,
} from '@/lib/heroSlides';
import { isWhatsAppUrl } from '@/lib/contactActions';
import { optimizeImageSrcSet, optimizeImageUrl, restoreOriginalImage } from '@/lib/imageUrl';
import { CDI_AGE_VARIABLE, interpolateCdiAge } from '../../shared/institutionalAge.js';

// React 18 forwards this native attribute only when it is lowercase.
const HIGH_PRIORITY_IMAGE_PROPS = { fetchpriority: 'high' };

// URLs de slides já pré-carregadas na sessão, para não acumular <link> duplicado.
const prefetchedHeroImages = new Set();

export default function HeroSlider({ onReady }) {
  useCdiAge();
  const { path, resolvePath, labelForPath } = usePublicRoutes();
  const [slides, setSlides] = useState([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [videoPlaybackAllowed, setVideoPlaybackAllowed] = useState(false);
  const [failedVideoUrls, setFailedVideoUrls] = useState(() => new Set());
  const [autoplayPaused, setAutoplayPaused] = useState(false);
  const [tabHidden, setTabHidden] = useState(false);
  const [controlsHovered, setControlsHovered] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const videoRefs = useRef(new Map());
  const firstSlideReady = useRef(false);
  const touchStart = useRef(null);

  const notifyReady = useCallback(() => {
    if (firstSlideReady.current) return;
    firstSlideReady.current = true;
    onReady?.();
  }, [onReady]);

  useEffect(() => {
    let active = true;
    listRows('hero_slides', { filters: { ativo: true }, orderBy: 'ordem', ascending: true, limit: 20 })
      .then(data => { if (active) setSlides(data); })
      .catch(() => { if (active) setSlides([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const networkNavigator = /** @type {any} */ (navigator);
    const connection = networkNavigator.connection || networkNavigator.mozConnection || networkNavigator.webkitConnection;
    const motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const updatePreference = () => {
      setVideoPlaybackAllowed(canAutoplayHeroVideo({
        saveData: Boolean(connection?.saveData),
        reducedMotion: Boolean(motionQuery?.matches),
      }));
    };

    updatePreference();
    setReducedMotion(Boolean(motionQuery?.matches));
    connection?.addEventListener?.('change', updatePreference);
    motionQuery?.addEventListener?.('change', updatePreference);
    return () => {
      connection?.removeEventListener?.('change', updatePreference);
      motionQuery?.removeEventListener?.('change', updatePreference);
    };
  }, []);

  useEffect(() => {
    const handleVisibility = () => setTabHidden(Boolean(document.hidden));
    handleVisibility();
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const next = useCallback(() => setCurrent(c => (c + 1) % slides.length), [slides.length]);
  const prev = useCallback(() => setCurrent(c => (c - 1 + slides.length) % slides.length), [slides.length]);

  // A rotação pausa com a aba oculta, o mouse sobre os controles, foco em um
  // controle ou pausa manual. Hover/foco não pausam o vídeo ativo.
  const autoplayStopped = autoplayPaused || tabHidden || controlsHovered;

  useEffect(() => {
    if (slides.length < 2 || autoplayStopped) return;
    const timer = setTimeout(next, heroSlideDurationMs(slides[current]));
    return () => clearTimeout(timer);
  }, [current, next, slides, autoplayStopped]);

  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (index === current || !video) return;
      video.pause();
      video.removeAttribute('src');
      video.load();
    });
  }, [current]);

  // Acompanha a pausa explícita e a visibilidade da aba no vídeo ativo.
  useEffect(() => {
    const video = videoRefs.current.get(current);
    if (!video) return;
    if (autoplayPaused || tabHidden) video.pause();
    else if (videoPlaybackAllowed) video.play().catch(() => {});
  }, [autoplayPaused, tabHidden, current, videoPlaybackAllowed]);

  useEffect(() => {
    if (!loading && slides.length === 0) notifyReady();
  }, [loading, notifyReady, slides.length]);

  // Pré-carrega a imagem do próximo slide com o mesmo srcset da exibição, em
  // baixa prioridade para não disputar banda com a imagem visível. Assim a
  // troca de slide não abre uma "janela" sem imagem.
  useEffect(() => {
    if (slides.length < 2) return;
    const nextSlide = resolveHeroSlide(slides[(current + 1) % slides.length]);
    const url = nextSlide?.imagem_url;
    if (!url || prefetchedHeroImages.has(url)) return;
    prefetchedHeroImages.add(url);
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.setAttribute('imagesrcset', optimizeImageSrcSet(url, [640, 960, 1280, 1920], { quality: 75 }));
    link.setAttribute('imagesizes', '100vw');
    link.fetchPriority = 'low';
    document.head.appendChild(link);
  }, [current, slides]);

  const handleControlsPointerEnter = useCallback((event) => {
    if (event.pointerType === 'mouse') setControlsHovered(true);
  }, []);

  const handleControlsPointerLeave = useCallback((event) => {
    if (event.pointerType === 'mouse') setControlsHovered(false);
  }, []);

  const handleControlsBlur = useCallback((event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setControlsHovered(false);
  }, []);

  const handleTouchStart = useCallback((event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    // Toques em controles interativos nunca devem virar swipe.
    if (event.target.closest('a, button, input')) {
      touchStart.current = null;
      return;
    }
    touchStart.current = { x: touch.clientX, y: touch.clientY };
  }, []);

  const handleTouchEnd = useCallback((event) => {
    const start = touchStart.current;
    touchStart.current = null;
    const touch = event.changedTouches?.[0];
    if (!start || !touch) return;
    const direction = resolveHeroSwipe(start.x, start.y, touch.clientX, touch.clientY);
    if (direction === 'next') next();
    else if (direction === 'prev') prev();
  }, [next, prev]);

  if (loading || slides.length === 0) {
    // Fallback hero estático
    return (
      <section className="relative flex h-[calc(100dvh-80px)] items-center overflow-hidden bg-cdi-blue lg:h-[calc(100dvh-140px)]">
        <div className="absolute inset-0 bg-gradient-to-r from-cdi-blue via-cdi-blue/90 to-transparent z-10" />
        <div className="relative z-20 max-w-7xl mx-auto px-8">
          <div className="inline-block bg-cdi-yellow px-4 py-1 mb-5">
            <span className="text-cdi-blue font-black text-xs tracking-widest uppercase">{interpolateCdiAge(HERO_FALLBACK_BADGE)}</span>
          </div>
          <h1 className="min-h-[6rem] text-5xl font-black uppercase leading-none text-white md:min-h-[14rem] md:text-7xl mb-6">
            CDI<br />PREMIUM
          </h1>
          <p className="min-h-[3.5rem] max-w-xl text-lg font-medium leading-relaxed text-white/80">
            Diagnósticos por imagem com tecnologia, precisão e cuidado em Goiânia.
          </p>
          <div className="flex gap-4 flex-wrap mt-8">
            <Link to={path('schedule')}
              className="flex items-center gap-3 px-7 py-4 bg-cdi-yellow text-cdi-blue font-black text-sm uppercase tracking-wider"
              style={{ boxShadow: '5px 5px 0px rgba(255,255,255,0.3)' }}>
              <WhatsAppIcon size={18} />
              Agendar pelo WhatsApp
            </Link>
          </div>
          <HeroExamSearch />
        </div>
      </section>
    );
  }

  const storedSlide = slides[current];
  const slide = resolveHeroSlide(storedSlide);
  const heroImage = optimizeImageUrl(slide.imagem_url, { width: 1280, quality: 75 });
  const heroImageSrcSet = optimizeImageSrcSet(slide.imagem_url, [640, 960, 1280, 1920], { quality: 75 });
  const kenBurnsSeconds = Math.min(heroSlideDurationMs(storedSlide) / 1000, 15);
  const showActiveVideo = isHeroVideoSlide(storedSlide)
    && Boolean(slide.video_url)
    && videoPlaybackAllowed
    && !failedVideoUrls.has(slide.video_url);
  const ctaIsWhatsApp = isWhatsAppUrl(slide.cta_url);
  const ctaLabel = ctaIsWhatsApp
    ? 'Agendar pelo WhatsApp'
    : storedSlide.cta_label?.includes(CDI_AGE_VARIABLE)
    ? slide.cta_label
    : labelForPath(slide.cta_url, slide.cta_label);

  return (
    <section
      className="relative h-[calc(100dvh-80px)] touch-pan-y overflow-hidden bg-cdi-blue lg:h-[calc(100dvh-140px)]"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Slides */}
      <AnimatePresence initial={false} mode="wait">
        <motion.div
          key={current}
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7 }}
          className="absolute inset-0"
        >
          <motion.img
            initial={{ scale: 1 }}
            animate={{ scale: reducedMotion ? 1 : 1.07 }}
            transition={{ duration: kenBurnsSeconds, ease: 'linear' }}
            src={heroImage}
            srcSet={heroImageSrcSet}
            sizes="100vw"
            alt={slide.alt}
            width="1920"
            height="1080"
            loading="eager"
            {...HIGH_PRIORITY_IMAGE_PROPS}
            decoding="async"
            onLoad={notifyReady}
            onError={(event) => {
              const originalAlreadyRestored = event.currentTarget.dataset.originalRestored === 'true';
              restoreOriginalImage(event, slide.imagem_url);
              if (originalAlreadyRestored) notifyReady();
            }}
            className="w-full h-full object-cover"
          />
          {showActiveVideo && (
            <video
              ref={(element) => {
                if (element) videoRefs.current.set(current, element);
                else videoRefs.current.delete(current);
              }}
              src={slide.video_url}
              autoPlay
              muted
              playsInline
              loop
              preload="none"
              disablePictureInPicture
              controls={false}
              aria-hidden="true"
              onError={() => setFailedVideoUrls(previous => new Set(previous).add(slide.video_url))}
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-cdi-yellow" />
        </motion.div>
      </AnimatePresence>

      {/* Decoração geométrica */}
      <div className="absolute inset-0 z-10 bg-black/55" />

      {/* Conteúdo */}
      {!slide.ocultar_conteudo && (
        <div className="relative z-20 flex h-full items-center justify-center px-7">
          <div className="w-full max-w-3xl text-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={`content-${current}`}
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -22 }}
                transition={{ duration: 0.5, delay: 0.15 }}
                className="mx-auto max-w-2xl"
              >
                <div className="mb-5 inline-block bg-cdi-yellow px-4 py-1.5">
                  <span className="text-xs font-black uppercase tracking-widest text-cdi-blue">{interpolateCdiAge(HERO_FALLBACK_BADGE)}</span>
                </div>
                {slide.titulo && (
                  <h1 className="mb-5 text-3xl font-black leading-tight text-white sm:text-4xl md:text-5xl">
                    {slide.titulo}
                  </h1>
                )}
                {slide.subtitulo && (
                  <p className="mx-auto max-w-xl text-base font-medium leading-7 text-white/90 sm:text-lg sm:leading-relaxed">
                    {slide.subtitulo}
                  </p>
                )}
                <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
                  {slide.cta_url && slide.cta_label && (
                    ctaIsWhatsApp ? (
                      <Link to={path('schedule')}
                        className="flex w-full items-center justify-center gap-3 bg-cdi-yellow px-7 py-4 text-sm font-black uppercase tracking-wider text-cdi-blue transition-colors hover:bg-white sm:w-auto">
                        <WhatsAppIcon size={18} />
                        {ctaLabel}
                      </Link>
                    ) : slide.cta_url.startsWith('http') ? (
                      <a href={slide.cta_url} target="_blank" rel="noopener noreferrer"
                        className="flex w-full items-center justify-center gap-3 bg-cdi-yellow px-7 py-4 text-sm font-black uppercase tracking-wider text-cdi-blue transition-colors hover:bg-white sm:w-auto">
                        <ArrowRight size={18} strokeWidth={2.5} />
                        {ctaLabel}
                      </a>
                    ) : (
                      <Link to={resolvePath(slide.cta_url)}
                        className="flex w-full items-center justify-center gap-3 bg-cdi-yellow px-7 py-4 text-sm font-black uppercase tracking-wider text-cdi-blue transition-colors hover:bg-white sm:w-auto">
                        <ArrowRight size={18} strokeWidth={2.5} />
                        {ctaLabel}
                      </Link>
                    )
                  )}
                  <a href="tel:6231217272" className="flex w-full items-center justify-center gap-3 border-2 border-white/80 px-7 py-4 text-sm font-black uppercase tracking-wider text-white transition-colors hover:border-cdi-yellow hover:bg-cdi-yellow hover:text-cdi-blue sm:w-auto">
                    <Phone size={18} strokeWidth={2.5} />
                    (62) 3121-7272
                  </a>
                </div>
              </motion.div>
            </AnimatePresence>

            <HeroExamSearch />
          </div>
        </div>
      )}

      {/* Controles */}
      {slides.length > 1 && (
        <div
          className="pointer-events-none absolute inset-0 z-30"
          onPointerEnter={handleControlsPointerEnter}
          onPointerLeave={handleControlsPointerLeave}
          onFocusCapture={() => setControlsHovered(true)}
          onBlurCapture={handleControlsBlur}
        >
          <button type="button" onClick={prev} aria-label="Slide anterior"
            className="pointer-events-auto absolute left-4 top-1/2 z-30 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-cdi-yellow text-cdi-blue transition-transform hover:scale-110 sm:left-8 sm:flex sm:h-12 sm:w-12">
            <ChevronLeft size={20} strokeWidth={3} />
          </button>
          <button type="button" onClick={next} aria-label="Próximo slide"
            className="pointer-events-auto absolute right-4 top-1/2 z-30 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-cdi-yellow text-cdi-blue transition-transform hover:scale-110 sm:right-8 sm:flex sm:h-12 sm:w-12">
            <ChevronRight size={20} strokeWidth={3} />
          </button>

          {/* Dots */}
          <div className="pointer-events-auto absolute bottom-7 left-1/2 z-10 flex -translate-x-1/2 gap-1.5">
            {slides.map((_, i) => (
              <button type="button" key={i} onClick={() => setCurrent(i)} aria-label={`Ir para o slide ${i + 1}`}
                className={`h-1 rounded-full transition-all ${i === current ? 'w-7 bg-cdi-yellow' : 'w-2 bg-white/45 hover:bg-white/70'}`}
              />
            ))}
          </div>

          {/* Pausa/retomada e contador */}
          <div className="pointer-events-auto absolute bottom-5 right-4 z-10 flex items-center gap-3 md:bottom-8 md:right-10">
            <button
              type="button"
              onClick={() => setAutoplayPaused(previous => !previous)}
              aria-label={autoplayPaused ? 'Retomar rotação automática' : 'Pausar rotação automática'}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm transition-colors hover:bg-white/30 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cdi-yellow"
            >
              {autoplayPaused ? <Play size={15} strokeWidth={2.5} aria-hidden="true" /> : <Pause size={15} strokeWidth={2.5} aria-hidden="true" />}
            </button>
            {slides.length > 2 && (
              <span className="hidden text-xs font-black tracking-widest text-white/60 md:inline">
                {String(current + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
